/**
 * Russian grammatical number.
 *
 * Russian selects one of three noun forms from the last digits of the count:
 * «1 платёж», «2 платежа», «5 платежей» — and 11–14 take the third form even
 * though they end in 1–4. Every place in the interface where a number meets a
 * noun goes through here, because a hard-coded «платежей» after every figure is
 * the loudest possible signal that copy was translated by machine.
 */

/** `[one, few, many]` — the forms for 1, 2–4 and 5+ respectively. */
export type PluralForms = readonly [one: string, few: string, many: string];

export function plural(count: number, forms: PluralForms): string {
  const n = Math.abs(Math.trunc(count));
  const lastTwo = n % 100;
  // 11–14 are the exception: they take the "many" form despite ending in 1–4.
  if (lastTwo >= 11 && lastTwo <= 14) return forms[2];

  const lastOne = n % 10;
  if (lastOne === 1) return forms[0];
  if (lastOne >= 2 && lastOne <= 4) return forms[1];
  return forms[2];
}

/** «3 платежа» — the count and its correctly inflected noun. */
export function pluralize(count: number, forms: PluralForms): string {
  return `${count} ${plural(count, forms)}`;
}

/**
 * Nouns the interface counts. Kept together so a form is written once and
 * cannot drift between screens.
 */
export const NOUN = {
  payment: ['платёж', 'платежа', 'платежей'],
  transaction: ['транзакция', 'транзакции', 'транзакций'],
  card: ['карта', 'карты', 'карт'],
  file: ['файл', 'файла', 'файлов'],
  character: ['символ', 'символа', 'символов'],
  digit: ['цифра', 'цифры', 'цифр'],
  minute: ['минута', 'минуты', 'минут'],
  session: ['сессия', 'сессии', 'сессий'],
  dispute: ['спор', 'спора', 'споров'],
  entry: ['запись', 'записи', 'записей'],
  withdrawal: ['вывод', 'вывода', 'выводов'],
} as const satisfies Record<string, PluralForms>;

/** Verb agreement for a counted subject: «1 платёж ожидает», «3 платежа ожидают». */
export const VERB = {
  awaits: ['ожидает', 'ожидают', 'ожидают'],
  arrived: ['поступил', 'поступило', 'поступило'],
  added: ['добавлен', 'добавлено', 'добавлено'],
} as const satisfies Record<string, PluralForms>;
